import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { loadP5Source } from "@/lib/p5source";

/**
 * Runs a p5 sketch inside a sandboxed iframe.
 *
 * Sketch code is arbitrary JavaScript authored through the admin panel and
 * stored in D1. It is deliberately NEVER evaluated in the site's own
 * context. The frame is created with sandbox="allow-scripts" and WITHOUT
 * allow-same-origin, which puts it in an opaque origin, so a sketch cannot:
 *
 *   - read or modify the surrounding page's DOM
 *   - read localStorage or document.cookie for this origin
 *   - call /api/* carrying the signed-in user's session cookie
 *
 * That last one is the important one: without it, anyone who can write a
 * sketch could have a visiting owner's browser silently call the admin API.
 *
 * The two sandbox flags must stay exactly as they are. Adding
 * allow-same-origin alongside allow-scripts lets the frame reach out and
 * remove its own sandbox attribute, defeating all of the above.
 */

interface Props {
  code: string;
  title: string;
  /** Fixed full-viewport layer behind the page, vs. an inline preview box. */
  variant?: "background" | "inline";
  className?: string;
  /** Receives the frame's window once loaded, for thumbnail capture. */
  onReady?: (frame: Window | null) => void;
}

function buildDocument(p5Source: string, code: string, width: number, height: number): string {
  // Nothing here is interpolated into an HTML attribute or a string
  // literal — both blocks are raw <script> bodies — so a sketch cannot
  // break out into the surrounding markup.
  return `<!doctype html>
<html>
<head>
<meta charset="utf-8">
<style>
  html, body { margin: 0; padding: 0; overflow: hidden; background: transparent; }
  canvas { display: block; }
</style>
<script>${p5Source}</script>
</head>
<body>
<script>
window.onerror = function (message, source, line) {
  // The frame is cross-origin, so this is the only way an error reaches
  // anyone. Surfaced in the browser console via the parent.
  try { parent.postMessage({ __sketchError: String(message) + ' (line ' + line + ')' }, '*'); } catch (e) {}
  return true;
};

/**
 * Size shim.
 *
 * A sandboxed srcdoc frame reports window.innerWidth/innerHeight as 0, so
 * p5 derives windowWidth/windowHeight of 0 and every sketch that calls
 * createCanvas(windowWidth, windowHeight) — which is nearly all of them —
 * silently produces a 0x0 canvas and draws nothing.
 *
 * The parent knows the real box, so it passes the dimensions in and keeps
 * them current on resize. windowWidth/windowHeight are redefined as
 * accessors: the setter is a deliberate no-op so p5's own assignments
 * during init cannot clobber the values (and cannot throw in strict mode).
 */
(function () {
  var W = ${Math.max(1, Math.round(width))};
  var H = ${Math.max(1, Math.round(height))};

  function define(name, get) {
    try {
      Object.defineProperty(window, name, { get: get, set: function () {}, configurable: true });
    } catch (e) {}
  }
  define('windowWidth', function () { return W; });
  define('windowHeight', function () { return H; });

  /**
   * Thumbnail capture.
   *
   * The parent cannot read this canvas — that is the whole point of the
   * sandbox — so the frame reads its own and posts the image back. A
   * sketch that taints its canvas (createCapture(VIDEO), a cross-origin
   * image) makes toDataURL throw; that is reported rather than swallowed.
   */
  window.addEventListener('message', function (event) {
    var data = event.data;
    if (!data || !data.__sketchCapture) return;
    var token = data.token;
    var minFrames = data.minFrames || 30;
    var deadline = Date.now() + (data.waitMs || 12000);

    function grab() {
      try {
        var canvas = document.querySelector('canvas');
        if (!canvas) throw new Error('sketch drew no canvas');

        // Wait for the sketch to have actually rendered. A frame that is
        // scrolled out of view has its animation frames throttled, so
        // draw() may not have run once no matter how long we sleep — hence
        // reporting frameCount back rather than trusting a fixed delay.
        var frames = typeof window.frameCount === 'number' ? window.frameCount : 0;
        if (frames < minFrames && Date.now() < deadline) {
          setTimeout(grab, 250);
          return;
        }

        var maxW = data.maxWidth || 640;
        var scale = Math.min(1, maxW / canvas.width);
        var out = document.createElement('canvas');
        out.width = Math.max(1, Math.round(canvas.width * scale));
        out.height = Math.max(1, Math.round(canvas.height * scale));
        var ctx = out.getContext('2d');
        // Sketches that never clear leave transparency; flat-fill first so
        // the thumbnail is not see-through over the page.
        ctx.fillStyle = data.background || '#111';
        ctx.fillRect(0, 0, out.width, out.height);
        ctx.drawImage(canvas, 0, 0, out.width, out.height);

        // Reject a uniform frame. A sketch can run happily and still render
        // nothing useful — pixel's webcam is blocked inside the sandbox, so
        // it draws flat black. A blank thumbnail is worse than none, because
        // the gallery's generated fallback at least distinguishes the card.
        var probe = ctx.getImageData(0, 0, out.width, out.height).data;
        var step = Math.max(4, Math.floor(probe.length / (4 * 2000)) * 4);
        var first = -1;
        var varied = false;
        for (var i = 0; i < probe.length; i += step) {
          var lum = probe[i] + probe[i + 1] + probe[i + 2];
          if (first < 0) first = lum;
          else if (Math.abs(lum - first) > 6) { varied = true; break; }
        }
        if (!varied) throw new Error('sketch rendered a blank frame');

        var uri = out.toDataURL('image/webp', 0.72);
        if (uri.indexOf('data:image/webp') !== 0) uri = out.toDataURL('image/png');
        parent.postMessage({ __sketchThumb: uri, token: token, frames: frames }, '*');
      } catch (err) {
        parent.postMessage({
          __sketchThumbError: String((err && err.message) || err),
          token: token
        }, '*');
      }
    }
    grab();
  });

  window.addEventListener('message', function (event) {
    var data = event.data;
    if (!data || !data.__sketchResize) return;
    W = data.w; H = data.h;
    // Give the sketch its own resize hook first; fall back to resizing the
    // canvas directly so sketches without windowResized() still adapt.
    try {
      if (typeof window.windowResized === 'function') window.windowResized();
      else if (typeof window.resizeCanvas === 'function') window.resizeCanvas(W, H);
    } catch (e) {}
  });
})();
</script>
<script>
${code}
</script>
</body>
</html>`;
}

export function SketchFrame({ code, title, variant = "inline", className, onReady }: Props) {
  const [p5Source, setP5Source] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    let cancelled = false;
    void loadP5Source()
      .then((src) => {
        if (!cancelled) setP5Source(src);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Track the host box. The frame is built once at the first known size;
  // later changes are pushed in by message so the sketch is not restarted.
  useEffect(() => {
    const node = variant === "background" ? null : hostRef.current;

    const measure = () => {
      const next =
        variant === "background"
          ? { w: window.innerWidth, h: window.innerHeight }
          : node
            ? { w: node.clientWidth, h: node.clientHeight }
            : null;
      if (!next || next.w === 0 || next.h === 0) return;
      setSize((prev) => (prev && prev.w === next.w && prev.h === next.h ? prev : next));
      frameRef.current?.contentWindow?.postMessage(
        { __sketchResize: true, w: next.w, h: next.h },
        "*",
      );
    };

    measure();

    if (variant === "background") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    if (!node) return;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [variant]);

  // Rebuilding srcdoc restarts the sketch, so it is keyed on the code and
  // the *initial* size only — subsequent resizes go over postMessage.
  const initialSize = useRef<{ w: number; h: number } | null>(null);
  if (size && !initialSize.current) initialSize.current = size;
  const boot = initialSize.current;

  const srcDoc = useMemo(
    () => (p5Source && boot ? buildDocument(p5Source, code, boot.w, boot.h) : null),
    [p5Source, code, boot],
  );

  const frameStyle: CSSProperties =
    variant === "background"
      ? {
          position: "fixed",
          inset: 0,
          width: "100%",
          height: "100%",
          border: "none",
          zIndex: -1,
          pointerEvents: "none",
        }
      : { width: "100%", height: "100%", border: "none", display: "block" };

  const frame = failed ? null : srcDoc ? (
    <iframe
      ref={frameRef}
      // No allow-same-origin — see the note above.
      sandbox="allow-scripts"
      srcDoc={srcDoc}
      title={title}
      onLoad={() => onReady?.(frameRef.current?.contentWindow ?? null)}
      aria-hidden={variant === "background"}
      tabIndex={variant === "background" ? -1 : undefined}
      style={frameStyle}
      className={className}
    />
  ) : null;

  // The background needs no host box: it is fixed to the viewport.
  if (variant === "background") return frame;

  return (
    <div ref={hostRef} style={{ width: "100%", height: "100%" }}>
      {failed ? (
        <div className="empty" style={{ padding: "var(--s-5)" }}>
          <p className="subtle">Could not load p5.</p>
        </div>
      ) : (
        frame
      )}
    </div>
  );
}

/** Forwards sketch errors into the host console, once per app. */
export function useSketchErrorLogging() {
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      const data = event.data as { __sketchError?: string } | null;
      if (data && typeof data.__sketchError === "string") {
        console.error("[sketch]", data.__sketchError);
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);
}

/**
 * Ask a loaded sketch frame for a thumbnail.
 *
 * Resolves with a data URI, or rejects if the sketch drew nothing, tainted
 * its canvas, or simply never answered.
 */
export function captureThumbnail(
  frame: Window,
  options: {
    maxWidth?: number;
    background?: string;
    timeoutMs?: number;
    /** Frames the sketch must have drawn before the grab. */
    minFrames?: number;
  } = {},
): Promise<{ dataUri: string; frames: number }> {
  const token = Math.random().toString(36).slice(2);
  const {
    maxWidth = 640,
    background = "#111111",
    timeoutMs = 20000,
    minFrames = 30,
  } = options;

  return new Promise((resolve, reject) => {
    const cleanup = () => {
      window.removeEventListener("message", onMessage);
      clearTimeout(timer);
    };
    const onMessage = (event: MessageEvent) => {
      const data = event.data as
        | { __sketchThumb?: string; __sketchThumbError?: string; token?: string; frames?: number }
        | null;
      if (!data || data.token !== token) return;
      cleanup();
      if (data.__sketchThumb) resolve({ dataUri: data.__sketchThumb, frames: data.frames ?? 0 });
      else reject(new Error(data.__sketchThumbError ?? "capture failed"));
    };
    const timer = setTimeout(() => {
      cleanup();
      reject(new Error("capture timed out"));
    }, timeoutMs);

    window.addEventListener("message", onMessage);
    frame.postMessage(
      { __sketchCapture: true, token, maxWidth, background, minFrames, waitMs: timeoutMs - 2000 },
      "*",
    );
  });
}
